<script setup>
import { ref, computed } from 'vue'
import PhoneInput from '@/Components/Forms/PhoneInput.vue'

const phoneRef = ref(null)

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Personal Information'
  },
  description: {
    type: String,
    default: 'Update your personal details and contact information'
  },
  fieldMapping: {
    type: Object,
    default: () => ({
      address: 'address1',
      postcode: 'zip'
    })
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const updateField = (field, value) => {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
  // Clear error for this field when user starts typing
  if (localErrors.value[field]) {
    delete localErrors.value[field]
  }
}

const validate = () => {
  const errors = {}
  const addressField = props.fieldMapping.address
  const postcodeField = props.fieldMapping.postcode

  if (!props.modelValue.first_name?.trim()) {
    errors.first_name = 'First name is required'
  }
  if (!props.modelValue.last_name?.trim()) {
    errors.last_name = 'Last name is required'
  }
  // Email is read-only, no validation needed
  if (!props.modelValue.phone?.trim()) {
    errors.phone = 'Phone number is required'
  } else if (phoneRef.value && !phoneRef.value.isValid()) {
    errors.phone = 'Please enter a valid phone number'
  }
  if (!props.modelValue.date_of_birth) {
    errors.date_of_birth = 'Date of birth is required'
  }
  if (!props.modelValue[addressField]?.trim()) {
    errors[addressField] = 'Address is required'
  }
  if (!props.modelValue.city?.trim()) {
    errors.city = 'City is required'
  }
  if (!props.modelValue[postcodeField]?.trim()) {
    errors[postcodeField] = 'Postcode is required'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="bg-white rounded-2xl p-4 md:p-10">
    <div class="flex items-center justify-between mb-8">
      <div>
        <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">{{ title }}</h2>
        <p class="hidden md:block text-gray-600">{{ description }}</p>
      </div>
      <div class="flex items-center space-x-2 text-sm text-gray-500">
        <span class="material-symbols-outlined">lock</span>
        <span class="hidden md:inline">Your data is secure</span>
      </div>
    </div>

    <div class="space-y-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div id="first-name-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            First Name <span class="text-red-500">*</span>
          </label>
          <input
            :value="modelValue.first_name"
            @input="updateField('first_name', $event.target.value)"
            type="text"
            required
            placeholder="John"
            class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900"
          />
          <p v-if="displayErrors.first_name" class="text-red-500 text-sm mt-1">{{ displayErrors.first_name }}</p>
        </div>

        <div id="last-name-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            Last Name <span class="text-red-500">*</span>
          </label>
          <input
            :value="modelValue.last_name"
            @input="updateField('last_name', $event.target.value)"
            type="text"
            required
            placeholder="Smith"
            class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900"
          />
          <p v-if="displayErrors.last_name" class="text-red-500 text-sm mt-1">{{ displayErrors.last_name }}</p>
        </div>
      </div>

      <div id="email-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          Email Address
        </label>
        <div class="relative">
          <span class="material-symbols-outlined absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400">mail</span>
          <input
            :value="modelValue.email"
            type="email"
            readonly
            disabled
            class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>
        <p class="text-xs text-gray-500 mt-2 flex items-center">
          <span class="material-symbols-outlined text-sm mr-1">info</span>
          Contact support to change your email address
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div id="phone-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            Phone Number <span class="text-red-500">*</span>
          </label>
          <PhoneInput
            ref="phoneRef"
            :model-value="modelValue.phone"
            :invalid="Boolean(displayErrors.phone)"
            @update:model-value="(value) => updateField('phone', value)"
          />
          <p v-if="displayErrors.phone" class="text-red-500 text-sm mt-1">{{ displayErrors.phone }}</p>
        </div>

        <div id="dob-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            Date of Birth <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <span class="material-symbols-outlined absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400">calendar_month</span>
            <input
              :value="modelValue.date_of_birth"
              @input="updateField('date_of_birth', $event.target.value)"
              type="date"
              required
              class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none"
            />
          </div>
          <p v-if="displayErrors.date_of_birth" class="text-red-500 text-sm mt-1">{{ displayErrors.date_of_birth }}</p>
        </div>
      </div>

      <div id="address-field">
        <label class="block text-sm font-semibold text-gray-700 mb-3">
          First Line of Address <span class="text-red-500">*</span>
        </label>
        <input
          :value="modelValue[fieldMapping.address]"
          @input="updateField(fieldMapping.address, $event.target.value)"
          type="text"
          required
          placeholder="Street address"
          class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900"
        />
        <p v-if="displayErrors[fieldMapping.address]" class="text-red-500 text-sm mt-1">{{ displayErrors[fieldMapping.address] }}</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div id="city-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            City <span class="text-red-500">*</span>
          </label>
          <input
            :value="modelValue.city"
            @input="updateField('city', $event.target.value)"
            type="text"
            required
            placeholder="London"
            class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900"
          />
          <p v-if="displayErrors.city" class="text-red-500 text-sm mt-1">{{ displayErrors.city }}</p>
        </div>

        <div id="postcode-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            Postcode <span class="text-red-500">*</span>
          </label>
          <input
            :value="modelValue[fieldMapping.postcode]"
            @input="updateField(fieldMapping.postcode, $event.target.value)"
            type="text"
            required
            placeholder="SW1A 1AA"
            class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 uppercase"
          />
          <p v-if="displayErrors[fieldMapping.postcode]" class="text-red-500 text-sm mt-1">{{ displayErrors[fieldMapping.postcode] }}</p>
        </div>

        <div id="country-field">
          <label class="block text-sm font-semibold text-gray-700 mb-3">
            Country <span class="text-red-500">*</span>
          </label>
          <select
            :value="modelValue.country"
            @input="updateField('country', $event.target.value)"
            class="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 appearance-none bg-white"
          >
            <option>United Kingdom</option>
            <option>Ireland</option>
            <option>Other</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>
